import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Zap } from 'lucide-react';

const WhyDatadogSection = () => {
  const fitPoints = [
    { label: 'Fortune 1000 Ready', detail: 'Sold to enterprises at Avalara ($100K+ deals), Zoom (embedded SDK), Icertis ($250M to $2B segment)', color: '#8B5CF6' },
    { label: 'Cloud-Native DNA', detail: 'Sold GCP, Google Workspace, Zoom SDK, and Salesforce. I understand the technical buyer intimately: their pain points, decision criteria, and what drives them to say yes', color: '#06B6D4' },
    { label: 'Full-Cycle Revenue Ownership', detail: 'Owned new logo acquisition, expansion, renewals, and partner-led growth. Built GTM plans, managed channels, and leveraged Salesforce and AI-driven tools across the full revenue cycle', color: '#A78BFA' },
    { label: 'AI-Era Ready', detail: 'Passionate about how AI is transforming enterprise tech stacks and how that creates new conversations, new budgets, and new opportunities for the right seller', color: '#22D3EE' },
    { label: 'Net-New Hunter Mindset', detail: 'The majority of my success has been built on new logo acquisition. 140+ new logos closed across 9.5 years and 6 companies', color: '#8B5CF6' },
    { label: 'Proven $1M+ Seller', detail: 'Closed $1.1M+ at Avalara and $1.125M+ at Zoom, consistently delivering above quota at every company', color: '#06B6D4' }
  ];

  return (
    <section id="why-datadog" className="py-20 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#2A1F5E]/30 via-transparent to-[#2A1F5E]/30" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur-xl border-2 border-[#8B5CF6]/30 rounded-3xl p-10 relative overflow-hidden"
          data-testid="why-datadog-section"
          style={{ boxShadow: '0 0 60px rgba(139, 92, 246, 0.3)' }}
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B5CF6] rounded-full blur-3xl opacity-10" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl opacity-10" />
          
          <div className="relative z-10">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center mb-4">
                <div className="p-4 rounded-xl bg-white/5" style={{ boxShadow: '0 0 30px rgba(139, 92, 246, 0.5)' }}>
                  <Zap className="text-[#8B5CF6]" size={36} style={{ filter: 'drop-shadow(0 0 10px #8B5CF6)' }} />
                </div>
              </div>
              <motion.h2 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white mb-3"
                data-testid="why-datadog-heading"
              >
                Why I'm the Right Fit for Datadog
              </motion.h2>
              <div className="flex justify-center space-x-2">
                <div className="w-16 h-1 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4]" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {fitPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  whileHover={{ scale: 1.02, x: 5 }}
                  className="flex items-start space-x-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5"
                  data-testid={`why-datadog-card-${index}`}
                >
                  <div className="flex-shrink-0">
                    <div 
                      className="p-2 rounded-lg bg-white/5"
                      style={{ boxShadow: `0 0 15px ${point.color}40` }}
                    >
                      <CheckCircle 
                        size={20} 
                        style={{ color: point.color, filter: `drop-shadow(0 0 6px ${point.color})` }} 
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-white text-sm mb-1.5">{point.label}</div>
                    <div className="text-xs text-white/60 leading-relaxed">{point.detail}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyDatadogSection;
